import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import i18n, { LANGS, detectLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { pathFor } from "@/marketing/paths";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAMES = { it: "Italiano", es: "Español", en: "English" };

function pickLang(q) {
  if (LANGS.includes(q)) return q;
  try { const s = localStorage.getItem("musora_lang"); if (LANGS.includes(s)) return s; } catch { /* storage unavailable */ }
  return detectLang();
}

const errText = (t, e, kind) => {
  const s = e?.response?.status;
  if (s === 429) return t("err_rate", { count: Math.max(1, Math.ceil(Number(e.response.headers?.["retry-after"] || 60) / 60)) });
  if (s === 422) return t("err_fields");
  if (s === 410) return t("err_expired_link");
  if (s === 404) return t(kind === "status" ? "err_status_link" : "err_invalid_link");
  return t("err_generic");
};

function LangPicker({ lang, onPick }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-600">{i18n.getFixedT(lang, "site", "privacy_form")("language")}
      <select value={lang} onChange={(e) => onPick(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="privacy-public-lang">
        {LANGS.map((l) => <option key={l} value={l}>{NAMES[l]}</option>)}
      </select>
    </label>);
}

function Verify({ token, t }) {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/public/privacy-requests/verify/${token}`).then(() => setS({ ok: true })).catch((e) => setS({ e })); }, [token]);
  return <p className="text-sm text-slate-700" data-testid="privacy-verify-result">{!s ? "…" : s.ok ? t("verify_ok") : errText(t, s.e, "verify")}</p>;
}

function Status({ token, t }) {
  const [d, setD] = useState(null);
  const [e, setE] = useState(null);
  useEffect(() => { axios.get(`${API}/public/privacy-requests/status/${token}`).then(({ data }) => setD(data)).catch(setE); }, [token]);
  if (e) return <p className="text-sm text-slate-700" data-testid="privacy-status-error">{errText(t, e, "status")}</p>;
  if (!d) return <p className="text-sm text-slate-500">…</p>;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm" data-testid="privacy-status">
      <dt className="text-slate-500">{t("st_request")}</dt><dd data-testid="privacy-status-type">{t(`types.${d.type}`)}</dd>
      <dt className="text-slate-500">{t("st_status")}</dt><dd className="font-medium" data-testid="privacy-status-label">{t(`statuses.${d.status}`)}</dd>
      <dt className="text-slate-500">{t("st_received")}</dt><dd>{d.received_at}</dd>
      <dt className="text-slate-500">{t("st_due")}</dt><dd data-testid="privacy-status-due">{d.due_at}{d.extended ? ` ${t("st_extended")}` : ""}</dd>
      {d.closed_at && <><dt className="text-slate-500">{t("st_closed")}</dt><dd>{d.closed_at}</dd></>}
      <dt className="text-slate-500">{t("st_link")}</dt><dd>{d.link_expires_at}</dd>
      <p className="col-span-2 mt-2 text-xs text-slate-500">{t("st_note")}</p>
    </dl>);
}

function RequestForm({ t, lang }) {
  const [f, setF] = useState({ name: "", email: "", type: "accesso", relation: "cliente_di_un_cliente", business_hint: "", message: "", hp: "" });
  const [done, setDone] = useState(false);
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const send = () => { setBusy(true); setE(null); axios.post(`${API}/public/privacy-requests`, { ...f, lang }).then(() => setDone(true)).catch(setE).finally(() => setBusy(false)); };
  if (done) return <p className="text-sm text-slate-700" data-testid="privacy-public-done">{t("done")}</p>;
  const field = (k, testid) => <Input value={f[k]} onChange={(x) => set(k, x.target.value)} placeholder={t(k === "business_hint" ? "business" : k)} aria-label={t(k === "business_hint" ? "business" : k)} data-testid={testid} />;
  const sel = (k, group, testid) => (
    <select value={f[k]} onChange={(x) => set(k, x.target.value)} aria-label={t(k)} className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid={testid}>
      {Object.entries(t(group)).map(([key, v]) => <option key={key} value={key}>{v}</option>)}
    </select>);
  return <>
    <p className="text-sm text-slate-600">{t("intro")}</p>
    {field("name", "privacy-public-name")}{field("email", "privacy-public-email")}
    {sel("type", "types", "privacy-public-type")}{sel("relation", "relations", "privacy-public-relation")}
    {field("business_hint", "privacy-public-business")}
    <textarea value={f.message} onChange={(x) => set("message", x.target.value)} rows={4} placeholder={t("message")} aria-label={t("message")} className="w-full rounded-md border border-slate-200 p-2 text-sm" data-testid="privacy-public-message" />
    <input value={f.hp} onChange={(x) => set("hp", x.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />
    {e && <p className="text-sm text-red-700" data-testid="privacy-public-error">{errText(t, e, "form")}</p>}
    <Button onClick={send} disabled={busy} data-testid="privacy-public-submit">{busy ? t("sending") : t("submit")}</Button>
    <p className="text-xs text-slate-500">{t("no_cookies")} <a href={pathFor(lang, "privacy")} className="underline" data-testid="privacy-public-policy-link">{t("policy")}</a></p>
  </>;
}

// Public channel for people WITHOUT a MUSORA account (customers of our clients, site visitors). No tracking, no cookies. IT/ES/EN.
export default function PrivacyRequestPage({ status = false }) {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const lang = pickLang(params.get("lang"));
  const t = i18n.getFixedT(lang, "site", "privacy_form");
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const pick = (l) => { try { localStorage.setItem("musora_lang", l); } catch { /* storage unavailable */ } setParams((p) => { p.set("lang", l); return p; }, { replace: true }); };
  return (
    <main className="mx-auto max-w-xl space-y-4 p-6" data-testid="privacy-public-page" lang={lang}>
      <div className="flex items-center justify-between gap-3"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">MUSORA</p><LangPicker lang={lang} onPick={pick} /></div>
      <h1 className="text-2xl font-semibold text-slate-900" data-testid="privacy-public-title">{t("title")}</h1>
      {token && status ? <Status token={token} t={t} /> : token ? <Verify token={token} t={t} /> : <RequestForm t={t} lang={lang} />}
    </main>);
}
